import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import {
  enumPurchaseOrderSortOption,
  enumPurchaseOrderStatus,
} from '@rematter/frontend-lib-api-client';
import { cn, humanReadableDateRange, MultiSelect } from '@rematter/ui-web';
import { atom, useAtomValue } from 'jotai';
import concat from 'lodash/concat';
import differenceBy from 'lodash/differenceBy';
import uniqBy from 'lodash/uniqBy';

import { purchaseOrdersListQueryAtom } from '../../_graphql/queryPurchaseOrdersList';

import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';
import { getLocale } from '~/shared/utils/locale';

export type PurchaseOrderOption = {
  id: string;
  documentNumber: string;
  account?: { name?: string | null } | null;
  startDate?: CalendarDate | null;
  endDate?: CalendarDate | null;
};

export type PurchaseOrderSelectProps = {
  isRequired?: boolean;
  label?: string;
  placeholder?: string;
  onSelectionChange?: (entries: PurchaseOrderOption[]) => void;
  selectedEntries?: PurchaseOrderOption[];
  className?: string;
  isDisabled?: boolean;
  accountIds?: string[];
};

function formatPurchaseOrderLabel(entity: PurchaseOrderOption): string {
  return [
    `#${entity.documentNumber}`,
    entity.account?.name,
    entity.startDate &&
      entity.endDate &&
      humanReadableDateRange(entity.startDate, entity.endDate, {
        locale: getLocale(),
      }),
  ]
    .filter(Boolean)
    .join(' ・ ');
}

export const PurchaseOrdersMultiSelect = ({
  isRequired = false,
  label = 'PO(s) #',
  placeholder = 'Select Purchase Orders',
  selectedEntries = [],
  onSelectionChange,
  className,
  isDisabled = false,
  accountIds,
}: PurchaseOrderSelectProps) => {
  const purchaseOrderFieldRef = React.useRef<HTMLDivElement>(null);

  const [$purchaseOrderSearchTerm] = React.useState(() => atom(''));

  const $purchaseOrders = React.useMemo(
    () =>
      purchaseOrdersListQueryAtom((get) => {
        const searchTerm = get($purchaseOrderSearchTerm);
        return {
          variables: {
            searchTerm: searchTerm,
            limit: 6,
            offset: 0,
            sorting: [enumPurchaseOrderSortOption.CreatedAtDesc],
            status: [
              enumPurchaseOrderStatus.Open,
              enumPurchaseOrderStatus.Closed,
            ],
            accountIds: accountIds,
            includeLineItems: false,
          },

          skip: false,
        };
      }),
    [$purchaseOrderSearchTerm, accountIds],
  );

  const { data } = useAtomValue($purchaseOrders);
  const [searchTerm, setSearchTerm] = useDebouncedAtom(
    $purchaseOrderSearchTerm,
    300,
  );

  const purchaseOrdersOptions = React.useMemo(() => {
    if (!searchTerm) {
      const difference = differenceBy(
        selectedEntries,
        data?.purchaseOrders ?? [],
        'id',
      );
      if (difference.length > 0) {
        return uniqBy(concat(difference, data?.purchaseOrders), 'id')
          .filter((entry) => entry !== undefined)
          .map((entry) => ({
            key: entry.id,
            label: formatPurchaseOrderLabel(entry),
          }));
      }
    }
    return uniqBy(concat(data?.purchaseOrders, selectedEntries), 'id')
      .filter((entry) => entry !== undefined)
      .map((entry) => ({
        key: entry.id,
        label: formatPurchaseOrderLabel(entry),
      }));
  }, [data?.purchaseOrders, selectedEntries, searchTerm]);

  const handleSelectionChange = React.useCallback(
    (keys: string[]) => {
      onSelectionChange?.(
        keys.map((key) => ({
          id: key,
          documentNumber:
            purchaseOrdersOptions.find((entry) => entry.key === key)?.label ??
            '',
        })),
      );
    },
    [onSelectionChange, purchaseOrdersOptions],
  );

  React.useEffect(
    () => () => {
      setSearchTerm('');
    },
    [setSearchTerm],
  );

  return (
    <MultiSelect
      ref={purchaseOrderFieldRef}
      label={label}
      placeholder={placeholder}
      options={purchaseOrdersOptions}
      onSelectionChange={handleSelectionChange}
      selectedKeys={selectedEntries.map((entry) => entry.id)}
      className={cn('@PurchaseOrdersMultiSelect', className)}
      onSearch={(query) => {
        setSearchTerm(query);
      }}
      required={isRequired}
      isDisabled={isDisabled}
    />
  );
};
